/** Route-aware return trails. Browser history restores the exact source URL. */
import { sectionForPath, activeTrail } from "./nav";

export interface Visit { path: string; label: string; key?: string }
export interface ReturnVisit extends Visit { delta: number }
const STORAGE = "hub.navigation-trail.v1";
const LIMIT = 100;
let byIndex: Map<number, Visit> | null = null;

/** Filters are not a new page. A drawer or a named page cut is. */
export function pageIdentity(path: string): string {
  const url = new URL(path, "http://hub.local");
  const query = new URLSearchParams();
  for (const key of ["v", "open", "txn"]) {
    const value = url.searchParams.get(key);
    if (value) query.set(key, value);
  }
  return url.pathname + (query.size ? "?" + query.toString() : "");
}

function visits(): Map<number, Visit> {
  if (byIndex) return byIndex;
  byIndex = new Map();
  try {
    const saved: unknown = JSON.parse(window.sessionStorage.getItem(STORAGE) ?? "[]");
    if (Array.isArray(saved)) for (const entry of saved.slice(-LIMIT)) {
      if (!Array.isArray(entry) || entry.length !== 2) continue;
      const [index, visit] = entry;
      if (!Number.isInteger(index) || index < 0 || !visit || typeof visit !== "object") continue;
      if (typeof visit.path !== "string" || !visit.path.startsWith("/") || visit.path.startsWith("//") || typeof visit.label !== "string") continue;
      byIndex.set(index, { path: pageIdentity(visit.path), label: visit.label.slice(0, 160), key: typeof visit.key === "string" ? visit.key : undefined });
    }
  } catch { /* Storage is optional; in-tab navigation still works. */ }
  return byIndex;
}

function idx(): number { return (window.history.state as { idx?: number } | null)?.idx ?? 0; }

export function labelForPath(path: string): string {
  const url = new URL(path, "http://hub.local");
  if (url.pathname === "/transactions") return url.searchParams.get("v") === "counterparties" ? "Counterparties" : "Bank transactions";
  if (url.pathname === "/connections") {
    const cut = url.searchParams.get("v");
    return cut === "institutions" ? "Institutions" : cut === "held" ? "Held for review" : "Connections";
  }
  const section = sectionForPath(url.pathname);
  if (!section) return "the previous page";
  const { tab, child } = activeTrail(section, url.pathname);
  return child?.label ?? tab?.label ?? section.label;
}

/** Persist only page identity/label, never search text or financial filter values. */
export function recordVisit(path: string, label: string, navigation: "PUSH" | "POP" | "REPLACE" = "PUSH"): void {
  const all = visits();
  const here = idx();
  const key = (window.history.state as { key?: string } | null)?.key;
  if (here === 0 && all.get(0)?.key !== key) all.clear();
  if (navigation === "PUSH") for (const index of all.keys()) if (index >= here) all.delete(index);
  all.set(here, { path: pageIdentity(path), label, key });
  const indexes = [...all.keys()].sort((a, b) => a - b);
  for (const index of indexes.slice(0, Math.max(0, indexes.length - LIMIT))) all.delete(index);
  try { window.sessionStorage.setItem(STORAGE, JSON.stringify([...all])); } catch { /* Storage can be disabled. */ }
}

export function findPreviousVisit(all: ReadonlyMap<number, Visit>, here: number, currentPath: string): ReturnVisit | null {
  const identity = pageIdentity(currentPath);
  for (let index = here - 1; index >= Math.max(0, here - LIMIT); index--) {
    const previous = all.get(index);
    // Never jump through an unknown history entry, which could be another site.
    if (!previous) return null;
    if (pageIdentity(previous.path) !== identity) return { ...previous, delta: here - index };
  }
  return null;
}

export function previousVisit(): ReturnVisit | null {
  if (typeof window === "undefined") return null;
  return findPreviousVisit(visits(), idx(), window.location.pathname + window.location.search);
}
